/**
 * 事務局→組織の学校共有ルーム（messageGroups.target_type=4）の発言に付く宛先（To）の表示判定と、To の候補の表示名。
 * 送信欄では「候補を全員選んだら To を送らない（指定なしと同じ）」の判定もここで持つ（isAllToCandidatesSelected。
 * サーバも保存時に同じ判定をするので、画面の判定が古い候補一覧に基づいていても届く相手は変わらない）。
 *
 * To の実体はサーバ側にある（messages.to_user_ids。保存時・通知時ともにサーバが組織のログイン可能な
 * 管理者との積を取る）。画面は get/detail・get/list が返す次の 2 つを読むだけで、宛先の可否を判定しない:
 *   - to_user_ids: 宛先 users.id の配列（To なしは null）
 *   - to_users:    [{id, username, flag, department, position, role}]（To がある発言にだけ付く。氏名と所属等は表示時にサーバが引く）
 *
 * ⚠️ 宛先の表記は本文（MessageDetail.vue は v-html で描画）に混ぜず、別要素にテキスト補間で描くこと。
 *    氏名を本文の HTML に足すと、氏名中の記号がそのまま HTML として解釈される。
 *
 * consultationLabel.js と同じく commonFunctions の肥大を避けるための専用 mixin。純関数のみ
 * （閲覧者は引数渡し・this は本 mixin の他メソッドの呼び出しにだけ使う）。
 */

import { AFFILIATION_VIEWER_ACCOUNT_TYPES, formatSchoolAdminAffiliation, formatSchoolAdminAffiliationBare } from '@/mixins/commonFunctions';

// 閲覧者が「あなた」の表示を受け取るロール（主管理者・副管理者）。事務局の画面には出さない
const ADDRESSEE_VIEWER_ACCOUNT_TYPES = [11, 12];

// 宛先まわりの画面の文言。見出しと選択肢は顧客の呼び方（「To」「指定なし」）にそろえている。ここだけ直せば済むように集める
export const TO_WORDING = {
  heading: 'To',
  all: '指定なし',
  pick: 'To を指定する',
  // 「To を指定する」で誰も選ばずに登録を押したときの alert（送信欄の下に常時出す説明は、顧客の依頼で置かない）
  pickEmpty: 'To にする人を 1 名以上選んでください',
  viewer: 'あなた',
};

export default {
  methods: {
    /**
     * 発言の宛先 id の配列を返す。配列でない・空なら []。
     * @param {{to_user_ids?: Array<number|string>|null}} chat 発言
     * @returns {number[]}
     */
    getMessageToUserIds(chat) {
      if (!chat || !Array.isArray(chat.to_user_ids)) return [];
      return chat.to_user_ids.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0);
    },
    /**
     * 発言の宛先の並び（名前の行に「事務局 → ○○さん（所属／職階／役割）、△△さん（…）」と出す）。To が無い発言は []（何も出さない）。
     * 所属等（affiliation）は投稿者の氏名の横と同じ書き方で、見せてよい閲覧者（11/12/31）のときだけ付ける。
     * 宛先がその後無効化されていたら所属等の後ろに「（無効）」を添え（inactiveLabel・投稿者表示と同じ並び）、
     * 氏名が引けない（ユーザーが残っていない）ときは「（退会した方）」と出す。
     * 閲覧者自身（管理者 11/12 のときだけ）は氏名の代わりに「あなた」と出し、所属等は付けない（isViewer=true）。
     * @param {{to_users?: Array<{id?: number, username?: string|null, flag?: number|null,
     *   department?: string|null, position?: string|null, role?: string|null}>}} chat 発言
     * @param {{id?: number, account_type?: number}|null} viewer 閲覧者（store の user）
     * @returns {Array<{key: string, label: string, affiliation: string, inactiveLabel: string, isViewer: boolean}>}
     */
    getMessageToNames(chat, viewer = null) {
      if (!chat || !Array.isArray(chat.to_users) || !chat.to_users.length) return [];
      const viewerId = viewer && ADDRESSEE_VIEWER_ACCOUNT_TYPES.includes(viewer.account_type) ? Number(viewer.id) : null;
      const showsAffiliation = !!viewer && AFFILIATION_VIEWER_ACCOUNT_TYPES.includes(viewer.account_type);
      return chat.to_users.map((toUser, i) => {
        const key = `${toUser && toUser.id != null ? toUser.id : 'x'}-${i}`;
        // 「あなた」と「（退会した方）」には所属等も（無効）も付けない
        const bare = { key, affiliation: '', inactiveLabel: '' };
        if (viewerId !== null && toUser && Number(toUser.id) === viewerId) return { ...bare, label: TO_WORDING.viewer, isViewer: true };
        if (!toUser || !toUser.username) return { ...bare, label: '（退会した方）', isViewer: false };
        const inactive = toUser.flag != null && Number(toUser.flag) !== 1;
        return {
          key,
          label: `${toUser.username}さん`,
          affiliation: showsAffiliation ? formatSchoolAdminAffiliation(toUser) : '',
          inactiveLabel: inactive ? '（無効）' : '',
          isViewer: false,
        };
      });
    },
    /**
     * 閲覧者自身が発言の To に入っているか（発言の左端の色の線）。
     * 印を出すのは管理者（11/12）の画面だけ。閲覧者が未解決（null）なら false。
     * @param {{to_user_ids?: Array<number|string>|null}} chat 発言
     * @param {{id?: number, account_type?: number}|null} viewer 閲覧者（store の user）
     * @returns {boolean}
     */
    isMessageAddressedToViewer(chat, viewer) {
      if (!viewer || !ADDRESSEE_VIEWER_ACCOUNT_TYPES.includes(viewer.account_type)) return false;
      return this.getMessageToUserIds(chat).includes(Number(viewer.id));
    },
    /**
     * To の選択肢の表示名。投稿者表示（commonFunctions.getPostUserDisplayName・getPostUserAffiliation）と同じ
     * 「管理者・氏名（所属／職階／役割）」「副管理者・氏名（…）」。送信前の確認ダイアログに使う。
     * @param {{username?: string, account_type?: number, department?: string|null, position?: string|null, role?: string|null}} candidate
     *   get/toCandidates の 1 行
     * @returns {string}
     */
    getToCandidateLabel(candidate) {
      if (!candidate) return '';
      const role = this.getToCandidateRole(candidate);
      const name = role ? `${role}・${candidate.username}` : (candidate.username || '');
      return `${name}${formatSchoolAdminAffiliation(candidate)}`;
    },
    /**
     * To の選択肢のカードで氏名の下に出す所属等（「所属／職階／役割」・空なら「要更新」）。
     * カードでは氏名と別の行に出すので、氏名の横の書き方（「（…）」）からカッコを外している（顧客の依頼）。
     * 選択肢を出すのは事務局の画面だけなので、閲覧者の判定はしない。
     * @param {{department?: string|null, position?: string|null, role?: string|null}} candidate get/toCandidates の 1 行
     * @returns {string}
     */
    getToCandidateAffiliation(candidate) {
      if (!candidate) return '';
      return formatSchoolAdminAffiliationBare(candidate);
    },
    /**
     * To の選択肢のカードに小さく出す役職（管理者 11・副管理者 12。ほかは ''）。
     * @param {{account_type?: number}} candidate get/toCandidates の 1 行
     * @returns {string}
     */
    getToCandidateRole(candidate) {
      if (!candidate) return '';
      if (candidate.account_type === 11) return '管理者';
      if (candidate.account_type === 12) return '副管理者';
      return '';
    },
    /**
     * 「To を指定する」で候補を全員選んでいるか。全員なら届く相手が「指定なし」と同じなので、
     * 確認ダイアログ・送信値とも「指定なし」と同じに扱う（サーバも全員の To は To なしで保存する）。
     * @param {{candidates: Array<{id: number}>, selectedIds: number[]}} args
     * @returns {boolean}
     */
    isAllToCandidatesSelected({ candidates = [], selectedIds = [] }) {
      return candidates.length > 0 && candidates.every((candidate) => selectedIds.includes(candidate.id));
    },
  },
};
